// Hàm này xuất ra 1 file có dạng header là 1 dòng tiu đề phụ trên đầu của bảng chính
import ExcelJS from "exceljs";
import {saveAs} from 'file-saver';
import DrawExcel from "@/commonFuncion/DrawExcel";

export async function exportToExcel(exportToExcelData) {
    const titleFile = exportToExcelData.titleFile;
    const columnHeaders = exportToExcelData.columnHeaders;
    const companyInfo = exportToExcelData.companyInfo;
    const columnWidths = exportToExcelData.columnWidths;
    const dataList = exportToExcelData.dataList;
    const fileName = exportToExcelData.nameFile;
    const classInSchool = exportToExcelData.className;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(fileName);

    // Apply column widths
    await DrawExcel.drawColumnWidths(columnWidths, worksheet);
    
    // Process companyInfo to:
    // 1. Remove Url lines.
    // 2. Separate Phone and Email.
    let processedCompanyInfo = [];
    if (Array.isArray(companyInfo)) {
        companyInfo.forEach(info => {
            if (info.toLowerCase().startsWith('url:')) {
                // Skip URL line
                return;
            }
            if (info.includes('SDT:') && info.includes('Email:')) {
                const parts = info.split(',');
                const sdtPart = parts[0].trim().replace('SDT:', 'SĐT:');
                const emailPart = parts[1] ? parts[1].trim() : '';
                processedCompanyInfo.push(sdtPart);
                if (emailPart) {
                    processedCompanyInfo.push(emailPart);
                }
            } else {
                processedCompanyInfo.push(info);
            }
        });
    } else {
        processedCompanyInfo = companyInfo;
    }

    // Draw company info (Rows 1-4)
    await DrawExcel.drawCompanyInfo(processedCompanyInfo, worksheet);
    
    // Draw title (Row 5)
    await DrawExcel.drawTitleRow(titleFile, worksheet);
    
    // Blank row (Row 5 already added title, we add Row 6 but wait, let's see how DrawExcel is implemented)
    // DrawCompanyInfo adds 4 rows (1, 2, 3, 4).
    // DrawTitleRow adds row 5.
    // Let's add an empty row 6? No, the code adds a blank row:
    // "await worksheet.addRow([]);" (which would be Row 6)
    // Then Row 7 is the merged header row, and Row 8 is the table header row.
    // Let's make sure the row indices match exactly. Let's see the previous code:
    // It calls DrawCompanyInfo, DrawTitleRow, worksheet.addRow([]), then Row 6 is onTopOfTheTableRow.
    // That means:
    // Row 1-4: Company info
    // Row 5: Title Row
    // DrawTitleRow adds it.
    // Then worksheet.addRow([]) adds Row 6.
    // But in the previous code, "onTopOfTheTableRow = worksheet.getRow(6)" was used.
    // And "for (let i = 6; i <= dataList.length + 7; i++)" set borders.
    // And "DrawTableHeaders" adds a row which becomes Row 7.
    // Let's follow this row layout but make it extremely robust and visually appealing.

    // Let's force clear styling for school info rows (1 to 4)
    for (let r = 1; r <= 4; r++) {
        const row = worksheet.getRow(r);
        row.height = 20;
        row.eachCell((cell) => {
            cell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FFC00000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
        });
    }

    // Title Row (Row 5)
    const titleRow = worksheet.getRow(5);
    titleRow.height = 28;
    titleRow.eachCell((cell) => {
        cell.font = { name: 'Segoe UI', size: 16, bold: true, color: { argb: 'FF1F497D' } }; // Elegant Dark Blue
        cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    });

    // Row 6 is the merged header row
    const groupHeaderRow = worksheet.getRow(6);
    groupHeaderRow.height = 26;

    // Define modern pastel colors
    const colors = {
        classBg: 'FFFDF2E9',      // Soft warm cream
        weightBg: 'FFE2EFDA',     // Soft pastel mint green
        heightBg: 'FFD9E1F2',     // Soft pastel sky blue
        bmiBg: 'FFEBF1DE',        // Soft pastel sage green
        noteBg: 'FFFCE4D6',       // Soft pastel light peach/rose
        headerBg: 'FFF2F2F2',     // Professional light gray
        textDark: 'FF333333',     // Elegant charcoal
        borderGray: 'FFD3D3D3',   // Soft gray border
    };

    const borderStyle = {
        top: { style: 'thin', color: { argb: colors.borderGray } },
        left: { style: 'thin', color: { argb: colors.borderGray } },
        bottom: { style: 'thin', color: { argb: colors.borderGray } },
        right: { style: 'thin', color: { argb: colors.borderGray } }
    };

    // Helper to style a merged group
    const styleMergedRange = (sheet, range, val, bg) => {
        sheet.mergeCells(range);
        const [startCellRef] = range.split(':');
        const cell = sheet.getCell(startCellRef);
        cell.value = val;
        cell.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: colors.textDark } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        
        // ExcelJS requires styling all cells in a merged range to render borders & backgrounds properly
        const [startCol, startRow, endCol, endRow] = decodeRange(range);
        for (let r = startRow; r <= endRow; r++) {
            for (let c = startCol; c <= endCol; c++) {
                const rangeCell = sheet.getCell(r, c);
                rangeCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: bg }
                };
                rangeCell.border = borderStyle;
            }
        }
    };

    // Decode Excel range like 'B6:I6' into [colStart, rowStart, colEnd, rowEnd] 1-indexed
    function decodeRange(rangeStr) {
        const parts = rangeStr.split(':');
        const start = parseCell(parts[0]);
        const end = parts[1] ? parseCell(parts[1]) : start;
        return [start.col, start.row, end.col, end.row];
    }

    function parseCell(cellStr) {
        const match = cellStr.match(/^([A-Z]+)([0-9]+)$/);
        const colStr = match[1];
        const row = parseInt(match[2], 10);
        let col = 0;
        for (let i = 0; i < colStr.length; i++) {
            col = col * 26 + (colStr.charCodeAt(i) - 64);
        }
        return { col, row };
    }

    // Apply merged header ranges on Row 6
    // Column A is empty (STT column top cell is merged down, but here we just leave it or style it)
    const sttTopCell = worksheet.getCell('A6');
    sttTopCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.headerBg } };
    sttTopCell.border = borderStyle;

    styleMergedRange(worksheet, 'B6:I6', classInSchool, colors.classBg);
    styleMergedRange(worksheet, 'J6:N6', "Đánh giá cân nặng theo tháng tuổi", colors.weightBg);
    styleMergedRange(worksheet, 'O6:S6', "Đánh giá chiều cao theo tháng tuổi", colors.heightBg);
    styleMergedRange(worksheet, 'T6:X6', "Đánh giá BMI theo tháng tuổi", colors.bmiBg);
    styleMergedRange(worksheet, 'Y6:Y6', "Ghi chú", colors.noteBg);

    // Row 7: Table Headers
    const headerRow = worksheet.getRow(7);
    headerRow.height = 30;
    columnHeaders.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: colors.headerBg }
        };
        cell.font = {
            name: 'Segoe UI',
            size: 10,
            bold: true,
            color: { argb: colors.textDark }
        };
        cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
        };
        cell.border = borderStyle;
    });

    // Row 8+: Data rows
    dataList.forEach((rowData) => {
        const addedRow = worksheet.addRow(rowData);
        addedRow.height = 22;
        
        addedRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.font = { name: 'Segoe UI', size: 10, color: { argb: 'FF444444' } };
            cell.border = borderStyle;
            
            // Highlight checkmarks 'x'
            if (cell.value === 'x') {
                cell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FF2E7D32' } }; // Forest Green for x
            }

            // Alignments based on column
            if (colNumber === 1) { // STT
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 2) { // Họ và tên
                cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
            } else if (colNumber === 3 || colNumber === 9) { // Ngày sinh, Ngày cân đo
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 4) { // Tuổi
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 5) { // Giới tính
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 6 || colNumber === 7 || colNumber === 8) { // Cân nặng, chiều cao, BMI
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                // Format numbers to look cleaner
                if (typeof cell.value === 'number') {
                    cell.numFmt = '#,##0.00';
                }
            } else if (colNumber >= 10 && colNumber <= 24) { // Evaluation marks 'x' or empty
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else { // Note
                cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
            }
        });
    });

    // Write buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], {type: 'application/octet-stream'}), fileName + '.xlsx');
}